/** Send a filing for approval, from a drawer on the filing (design review 2026-11-03, ux #3).
 *
 * The to-do list's "Send for approval" used to leave the filing for a generic record page, where a
 * person chose "Choose a route..." from a list of one and pressed "Submit revision 1 for review".
 * This is the same act - `POST /records/{id}/approval-requests`, the route as it stands now and the
 * revision on screen - done where the checks are. **Nothing new is decided here**: the routes are the
 * ones published for filings, the server refuses a route that does not apply, a submitter cannot
 * decide their own request, and an approval is not a statement that a report is lawfully fit to
 * file (F07).
 *
 * - **One route is preselected and shown as text**, with who reviews it, so the person is not asked to
 *   choose from a list of one. Several routes are a choice; none is a configuration gap, said once
 *   with who can fix it.
 * - **A request already open is said, not offered again**: with whom it is, at which stage.
 * - **A reviewer is named only to an Admin**: the roster is Admin-only (`PersonRef`), and every other
 *   role is told there is a named reviewer rather than shown an identifier.
 */

import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { currentRoutes } from "../../api/approvals";
import { useMe } from "../../api/auth";
import { useApprovalRoutes, useRecordWork, useSubmitForApproval } from "../../api/work";
import type { ApprovalRoute } from "../../api/work";
import { ReadFailed, WriteFailed } from "../../components/Loaded";
import { Drawer } from "../../components/ui/Drawer";
import { keepSearch } from "../search";
import type { ScreenSearch } from "../search";
import { isOpenRequest, useReviewerWords } from "./approvalRequest";
import styles from "./FilingParts.module.css";

function RouteLine({ route }: { route: ApprovalRoute }) {
  const reviewers = useReviewerWords(route.stages.map((stage) => stage.reviewer_id));
  return (
    <p className={styles.route} data-testid="send-route">
      <span className={styles.routeLabel}>Reviewed by</span>{" "}
      <strong>{reviewers}</strong> ({route.stages.length} stage{route.stages.length === 1 ? "" : "s"})
    </p>
  );
}

export function SendForApprovalDrawer({
  open,
  onClose,
  obligationId,
  title,
}: {
  open: boolean;
  onClose: () => void;
  /** The filing's obligation id: a filing is a record and this is its id (ADR-0029, ADR-0037). */
  obligationId: string;
  /** "Send CA SB 253 for approval". */
  title: string;
}) {
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  const work = useRecordWork(open ? obligationId : undefined);
  const routes = useApprovalRoutes("filing_obligation");
  const submit = useSubmitForApproval(obligationId);
  const [picked, setPicked] = useState("");
  const [refusal, setRefusal] = useState<unknown>(null);

  const request = work.data?.approval ?? null;
  const live = currentRoutes(routes.data ?? []);
  const only = live.length === 1 ? live[0] : undefined;
  const chosen = live.find((route) => route.id === picked) ?? only;
  const canWrite = me ? ["admin", "approver", "contributor"].includes(me.role) : undefined;
  const reviewerNow = useReviewerWords(
    request && isOpenRequest(request)
      ? request.stages.filter((stage) => stage.position === request.current_position).map((stage) => stage.reviewer_id)
      : [],
  );

  async function onSend() {
    if (!chosen) return;
    setRefusal(null);
    try {
      await submit.mutateAsync({ routeId: chosen.id, recordVersion: 1 });
      // The checks are a derived read: the approval step changes with this request, so ask again.
      await queryClient.invalidateQueries({ queryKey: ["filing-checks"] });
      setPicked("");
      onClose();
    } catch (failure) {
      setRefusal(failure);
    }
  }

  let body;
  if (work.error) {
    body = <ReadFailed of="This filing's approval" error={work.error} onRetry={() => void work.refetch()} />;
  } else if (routes.error) {
    body = (
      <ReadFailed
        of="The approval routes for filings"
        error={routes.error}
        onRetry={() => void routes.refetch()}
      />
    );
  } else if (work.isPending || routes.isPending) {
    body = <p className={styles.quiet}>Reading</p>;
  } else if (request && isOpenRequest(request)) {
    body = (
      <>
        <p className={styles.route} data-testid="send-already">
          <span className={styles.routeLabel}>With</span> <strong>{reviewerNow}</strong>
          {" "}(stage {request.current_position ?? "-"} of {request.stages.length})
        </p>
        <Link
          to="/records/$recordId"
          params={{ recordId: obligationId }}
          search={(prev: Partial<ScreenSearch>) => ({ ...keepSearch(prev), tab: "workflow" })}
          className={styles.link}
          onClick={onClose}
        >
          Open its history
        </Link>
      </>
    );
  } else if (canWrite === false) {
    body = (
      <p className={styles.quiet} data-testid="send-who-can">
        An Admin, an Approver or a Contributor sends a filing for approval.
      </p>
    );
  } else if (live.length === 0) {
    body = (
      <p className={styles.quiet} data-testid="send-no-route">
        {me?.role === "admin" ? (
          <>
            No approval route is published for filings.{" "}
            <Link
              to="/admin"
              search={(prev: Partial<ScreenSearch>) => ({ ...keepSearch(prev), tab: "routes" })}
              className={styles.link}
              onClick={onClose}
            >
              Publish one
            </Link>
          </>
        ) : (
          "No approval route is published for filings. An Admin publishes one."
        )}
      </p>
    );
  } else {
    body = (
      <>
        {only ? (
          <RouteLine route={only} />
        ) : (
          <label className={styles.field}>
            <span>Route</span>
            <select
              aria-label="Approval route"
              value={picked}
              onChange={(event) => setPicked(event.target.value)}
              data-testid="send-route-select"
            >
              <option value="">Choose a route</option>
              {live.map((route) => (
                <option key={route.id} value={route.id}>
                  {route.name} ({route.stages.length} stage{route.stages.length === 1 ? "" : "s"})
                </option>
              ))}
            </select>
          </label>
        )}
        {chosen && !only ? <RouteLine route={chosen} /> : null}
      </>
    );
  }

  const showSend = Boolean(
    !work.error && !routes.error && !work.isPending && !routes.isPending && !(request && isOpenRequest(request)) &&
      canWrite !== false && live.length > 0,
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={title}
      width="sm"
      testId="send-approval-drawer"
      footer={
        showSend ? (
          <button
            type="button"
            className={styles.primary}
            disabled={!chosen || submit.isPending}
            onClick={() => void onSend()}
            data-testid="send-approval-confirm"
          >
            {submit.isPending ? "Sending" : "Send for approval"}
          </button>
        ) : undefined
      }
    >
      <div className={styles.drawerBody}>
        {body}
        <WriteFailed error={refusal} of="this approval request" onReload={() => void work.refetch()} />
      </div>
    </Drawer>
  );
}
